import type {
  Quest,
  QuestGroup,
  QuestGroupState,
  QuestOrder,
} from "@ui-kit/QuestList";

/**
 * A utility function that generates a quest order object from the provided quest groups data.
 * This function extracts the order of quests within each quest group.
 * This object is important because users can skip quests, and we need to capture the quest
 * ordering. The resulting quest order object is designed to be stored in the user's local storage,
 * ensuring that the quest order is maintained even after the user reloads the application.
 *
 *  * Example output structure:
 *  * [
 *  *   {
 *  *     key: "level-1",
 *  *     quests: [
 *  *       { ruleId: "96191677-0a86-4718-a863-d8e7818fc063-wishlist-added" },
 *  *       { ruleId: "96191677-0a86-4718-a863-d8e7818fc063-trailer-watched" }
 *  *     ],
 *  *   },
 *  *   {
 *  *     key: "level-2",
 *  *     quests: [
 *  *       { ruleId: "96191677-0a86-4718-a863-d8e7818fc063-website-2-visited" },
 *  *     ]
 *  *   }
 *  * ]
 *
 * @param {Array<QuestGroupState | QuestGroup>} QuestGroupData - An array of quest groups.
 * @returns {QuestOrder} An object representing the order of quests within each group.
 */
export const createQuestOrder = (
  QuestGroupData: QuestGroupState[] | QuestGroup[],
): QuestOrder => {
  return QuestGroupData.map((level) => {
    const questsArray: Quest[] = level.quests || [];
    const quests = questsArray.map((quest) => ({
      ruleId: quest.ruleId,
    }));
    return {
      key: level.key,
      quests,
    };
  });
};
